import { getTime } from "~/tools/date-time";
import type { Time } from "~/tools/date-time/time";
import { filter } from "~/tools/filter";
import { identity } from "~/tools/identity";
import { not } from "~/tools/not";
import { pipe } from "~/tools/pipe";
import { prop } from "~/tools/prop";
import { RemoteData } from "~/tools/remote-data";
import { sortBy } from "~/tools/sort-by";
import { Tuple } from "~/tools/tuple";

import {
	Api,
	type OverviewColumnMeta,
	type OverviewFilters,
	type OverviewPageResponse,
	type OverviewRow,
	getOverviewFn,
} from "../common/openapi";

import type { InfiniteData, InfiniteQueryFunctionContext, UseFullInfiniteQueryOptions } from "./react-query";

interface OverviewPageParam {
	cursor?: string;
}

interface QueryOverviewParams extends OverviewFilters {
	urn: string;
	stql?: string;
	timestamp?: undefined | Time;
	sorting?: ReadonlyArray<Api.OverviewSorting>;
	limit?: number;
}

const queryOverview = (({ urn, stql, timestamp, tagFilters, columnFilters, sorting, limit }) => {
	const request = {
		query: stql,
		filters: { tagFilters, columnFilters },
		sorting: sorting?.slice(),
		pagination: { limit },
	} satisfies Partial<Api.OverviewRequest>;

	function queryFn({ pageParam }: InfiniteQueryFunctionContext<OverviewPageParam>) {
		return getOverviewFn({
			presentationOrViewUrn: urn,
			body: {
				...request,
				query: stql,
				topologyTime: getTime(timestamp),
				pagination: {
					...request.pagination,
					cursor: pageParam?.cursor
						? {
								cursor: pageParam.cursor,
								direction: Api.OverviewPaginationDirection.Forward,
							}
						: undefined,
				},
			},
		});
	}

	function getNextPageParam(lastPage: OverviewPageResponse) {
		if (lastPage._type !== "OverviewPageResult" || lastPage.pagination.endCursor == null) {
			return undefined;
		}

		return {
			cursor: lastPage.pagination.endCursor,
		};
	}

	return {
		timestamp,
		queryKey: Tuple("overview", urn, request),
		queryFn,
		getNextPageParam,
	} as const;
}) satisfies (params: QueryOverviewParams) => UseFullInfiniteQueryOptions<OverviewPageParam, Api.OverviewPageResponse>;

type QueryOverviewError = Error | Exclude<OverviewPageResponse, { _type: "OverviewPageResult" }>;

function isLockedMetricColumn({ display }: OverviewColumnMeta): boolean {
	return display._type === "MetricMetaDisplay" && display.locked;
}

interface QueryOverviewResponse {
	elementName: string;
	elementNamePlural: string;
	total: number;
	filtered: number;
	fixedColumns: number;
	columns: ReadonlyArray<OverviewColumnMeta>;
	rows: ReadonlyArray<OverviewRow>;
}

function toQueryOverviewResponse({
	hideUnavailableMetrics,
	error,
	data,
}: {
	hideUnavailableMetrics: undefined | boolean;
	error: null | Error;
	data: undefined | InfiniteData<OverviewPageResponse>;
}): RemoteData<QueryOverviewError, QueryOverviewResponse> {
	if (error != null) {
		return RemoteData.failure(error);
	}

	if (data == null || hideUnavailableMetrics == null) {
		return RemoteData.loading();
	}

	return RemoteData.all(
		data.pages.map((page) =>
			page._type === "OverviewPageResult" ? RemoteData.success(page) : RemoteData.failure(page),
		),
	).transform((pages) => {
		const lastPage = pages.at(-1);

		return {
			elementName: lastPage?.pagination.elementName ?? "",
			elementNamePlural: lastPage?.pagination.elementNamePlural ?? "",
			total: lastPage?.pagination.total ?? 0,
			filtered: lastPage?.pagination.filtered ?? lastPage?.pagination.total ?? 0,
			fixedColumns: lastPage?.metadata.fixedColumns ?? 0,
			columns: pipe(
				lastPage?.metadata.columns ?? [],
				hideUnavailableMetrics ? filter(not(isLockedMetricColumn)) : identity,
				sortBy(prop("order")),
			),
			rows: pages.flatMap(prop("data")),
		};
	});
}

export { queryOverview, toQueryOverviewResponse };
export type { OverviewPageParam, QueryOverviewParams, QueryOverviewError, QueryOverviewResponse };
